import React, { useState, useEffect } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { FilterState } from '../types';

interface SearchAndFiltersProps {
	onSearch: (value: string) => void;
	searchValue: string;
	filterState: FilterState;
	onFilterChange: (updates: Partial<FilterState>) => void;
	totalCount: number;
	filteredCount: number;
	loading: boolean;
	onCreateUser?: () => void;
}

export const SearchAndFilters: React.FC<SearchAndFiltersProps> = ({
	onSearch,
	searchValue,
	filterState,
	onFilterChange,
	totalCount,
	filteredCount,
	loading,
	onCreateUser
}) => {
	const [searchInput, setSearchInput] = useState(searchValue || '');

	useEffect(() => {
		setSearchInput(searchValue || '');
	}, [searchValue]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		const normalizedValue = searchInput.trim();
		setSearchInput(normalizedValue);
		onSearch(normalizedValue);
	};

	const isSearchDisabled = loading;

	return (
		<div className="space-y-4">
			{/* Search and Counter Row */}
			<div className="flex justify-between items-center gap-4">
				<form onSubmit={handleSearch} className="flex-1 flex gap-2" autoComplete="off">
					<div className="relative">
						<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
							<Search className="h-4 w-4 text-gray-400" />
						</div>
						<input
							type="text"
							id="user-accounts-search-input"
							name="user_search_safe_autofill_off"
							autoComplete="off"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							placeholder="Search users..."
							className="w-full rounded-md border py-2 pl-9 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
							{searchInput && (
								<button
									type="button"
									id="user-accounts-clear-search-button"
									onClick={() => {
										setSearchInput('');
									}}
									className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
								>
								<X className="h-5 w-5" />
							</button>
						)}
					</div>
					<button
						type="submit"
						id="user-accounts-search-button"
						disabled={isSearchDisabled}
						className={`px-4 py-2 rounded-md text-white ${isSearchDisabled
								? 'bg-gray-400 cursor-not-allowed'
								: 'bg-blue-500 hover:bg-blue-600'
							}`}
					>
						{loading ? 'Searching...' : 'Search'}
					</button>
				</form>

				<div className="flex items-center gap-3">
					<div className="text-sm text-gray-500">
						Showing {filteredCount} of {totalCount} users
					</div>
					{onCreateUser && (
						<button
							type="button"
							id="user-accounts-create-user-button"
							onClick={onCreateUser}
							className="px-3 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 text-sm"
						>
							Create User Account
						</button>
					)}
				</div>
			</div>

			{/* Filters Row */}
			<div className="flex flex-wrap items-center gap-4">
				{/* Status Filter */}
				<DropdownMenu>
					<DropdownMenuTrigger className="flex items-center px-3 py-2 text-sm border rounded-md hover:bg-gray-50">
						<SlidersHorizontal className="h-4 w-4 mr-2" />
						Filters
					</DropdownMenuTrigger>
					<DropdownMenuContent className="w-56">
						<DropdownMenuLabel>User Status</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuCheckboxItem
							checked={filterState.showDisabled}
							onCheckedChange={(checked) => onFilterChange({ showDisabled: checked })}
						>
							Show Disabled Users
						</DropdownMenuCheckboxItem>
						<DropdownMenuCheckboxItem
							checked={filterState.showLocked}
							onCheckedChange={(checked) => onFilterChange({ showLocked: checked })}
						>
							Show Locked Users
						</DropdownMenuCheckboxItem>
					</DropdownMenuContent>
				</DropdownMenu>

				<Select
					value={filterState.accountStatus || 'all'}
					onValueChange={(value) => {
						const normalized = value === 'all' ? '' : value;
						onFilterChange({ accountStatus: normalized });
					}}
				>
					<SelectTrigger className="w-[170px]">
						<SelectValue placeholder="Account status" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Accounts</SelectItem>
						<SelectItem value="active">Active</SelectItem>
						<SelectItem value="disabled">Disabled</SelectItem>
					</SelectContent>
				</Select>

				<Select
					value={filterState.lockedStatus || 'all'}
					onValueChange={(value) => {
						const normalized = value === 'all' ? '' : value;
						onFilterChange({ lockedStatus: normalized });
					}}
				>
					<SelectTrigger className="w-[170px]">
						<SelectValue placeholder="Lock status" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Lock States</SelectItem>
						<SelectItem value="locked">Locked</SelectItem>
						<SelectItem value="unlocked">Unlocked</SelectItem>
					</SelectContent>
				</Select>

				<Select
					value={filterState.twoFactorStatus || 'all'}
					onValueChange={(value) => {
						const normalized = value === 'all' ? '' : value;
						onFilterChange({ twoFactorStatus: normalized });
					}}
				>
					<SelectTrigger className="w-[200px]">
						<SelectValue placeholder="2FA status" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All 2FA</SelectItem>
						<SelectItem value="totp">TOTP</SelectItem>
						<SelectItem value="sso">SSO</SelectItem>
						<SelectItem value="disabled">Disabled</SelectItem>
					</SelectContent>
				</Select>

				<Select
					value={filterState.compliant || 'all'}
					onValueChange={(value) => {
						const normalized = value === 'all' ? '' : value;
						onFilterChange({ compliant: normalized });
					}}
				>
					<SelectTrigger className="w-[150px]">
						<SelectValue placeholder="Compliance" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Compliance</SelectItem>
						<SelectItem value="yes">Compliant</SelectItem>
						<SelectItem value="no">Non-compliant</SelectItem>
					</SelectContent>
				</Select>

				{/* Sort Options */}
				<Select
					value={`${filterState.sortBy === 'username' ? 'name' : filterState.sortBy}-${filterState.sortDirection}`}
					onValueChange={(value) => {
						const [field, direction] = value.split('-');
						onFilterChange({
							sortBy: field,
							sortDirection: direction as 'asc' | 'desc'
						});
					}}
				>
					<SelectTrigger className="w-[180px]">
						<SelectValue placeholder="Sort by" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="name-asc">Name (A-Z)</SelectItem>
						<SelectItem value="name-desc">Name (Z-A)</SelectItem>
						<SelectItem value="lastlogin-desc">Last Login (Recent)</SelectItem>
						<SelectItem value="lastlogin-asc">Last Login (Oldest)</SelectItem>
					</SelectContent>
				</Select>

				{/* Reset Filters Button */}
				{(filterState.showDisabled !== true ||
					filterState.showLocked !== true ||
					filterState.twoFactorStatus ||
					filterState.accountStatus ||
					filterState.lockedStatus ||
					filterState.compliant ||
					filterState.sortBy !== 'name' ||
					filterState.sortDirection !== 'asc') && (
						<button
							id="user-accounts-reset-filters-button"
							onClick={() => {
								onFilterChange({
									showDisabled: true,
									showLocked: true,
									twoFactorStatus: '',
									accountStatus: '',
									lockedStatus: '',
									compliant: '',
									sortBy: 'name',
									sortDirection: 'asc'
								});
								setSearchInput('');
								onSearch('');
							}}
							className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
						>
							Reset Filters
						</button>
					)}
			</div>
		</div>
	);
};

export default SearchAndFilters;
